import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ApiHost } from '@/lib/constants';
import { useAuthStore } from '@/stores/auth-store';
import { queryClient } from '@/lib/query';
import {
  configureRefreshTimer,
  startRefreshTimer,
  stopRefreshTimer,
} from '@/services/token-refresh';

const isDev = import.meta.env.VITE_APP_ENV === 'development';

/**
 * What the response interceptor rejects with: `{ status, data }`, as a real Error.
 *
 * `message` stays `undefined` on purpose. Callers write `err.message ?? fallback`,
 * and an Error's default `''` would beat their fallback and show an empty toast.
 */
export class HttpError extends Error {
  readonly status?: number;
  readonly data?: unknown;

  constructor(status?: number, data?: unknown) {
    super();
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
    Object.defineProperty(this, 'message', {
      value: undefined,
      writable: true,
      configurable: true,
    });
  }
}

// Single-flight refresh: every caller that needs a refresh while one is in
// flight shares the same promise, so we never send parallel POST /auth/refresh.
let refreshPromise: Promise<void> | null = null;

export const refreshSession = (): Promise<void> => {
  refreshPromise ??= axiosInstance
    .post(`${ApiHost.BackendService}/api/auth/refresh`, null, { withCredentials: true })
    .then((refreshResponse) => {
      const refreshData = refreshResponse.data as { expiresIn?: number };
      if (refreshData.expiresIn) {
        startRefreshTimer(refreshData.expiresIn);
      }
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
};

/** Gives up on a hung logout call, so the user still reaches /login. */
const LOGOUT_TIMEOUT_MS = 5_000;

let sessionEnding = false;

/**
 * The one real logout for a dead or forbidden session.
 *
 * It calls POST /auth/logout first, so the server clears both cookies. Without
 * that, /login sees a live /auth/me and sends the user straight back. Errors
 * are ignored: the user still lands on /login?to=<current page>.
 */
export const endSession = (): void => {
  if (sessionEnding) return;
  sessionEnding = true;
  stopRefreshTimer();
  const to = `${globalThis.location.pathname}${globalThis.location.search}`;
  axiosInstance
    .post(`${ApiHost.BackendService}/api/auth/logout`, null, {
      withCredentials: true,
      timeout: LOGOUT_TIMEOUT_MS,
      headers: { 'X-Skip-Refresh': '1' },
    })
    .catch(() => undefined)
    .finally(() => {
      queryClient.clear();
      useAuthStore.getState().clear();
      globalThis.location.href = `/login?to=${encodeURIComponent(to)}`;
    });
};

const isAuthPage = () => {
  const path = globalThis.location.pathname;
  return (
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/verify-email') ||
    path.startsWith('/forgot-password') ||
    path.startsWith('/set-password') ||
    path.startsWith('/impersonate')
  );
};

const onRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.debug('[HTTP Request]', config.method?.toUpperCase(), config.url);
  }
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return { data: response?.data, headers: response.headers } as AxiosResponse;
};

const onResponseError = async (error: AxiosError): Promise<unknown> => {
  const { status, data } = (error.response as AxiosResponse) ?? {};
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  // Opt-out: callers that set X-Skip-Refresh: 1 bypass the global 401 refresh
  // and 403 logout branches entirely; they handle classification locally.
  const skipRefresh =
    (originalRequest?.headers as Record<string, unknown> | undefined)?.['X-Skip-Refresh'] === '1';
  if (skipRefresh) {
    throw new HttpError(status, data);
  }

  // Skip interceptor handling for:
  // - /auth/impersonate — pre-auth consume; no session exists to refresh, and
  //   the page's mutation needs to see the error directly.
  // - /auth/refresh — never recurse on the refresh endpoint itself. A 401
  //   here means the Keycloak session is dead; the caller (timer or
  //   app-layout) handles redirect to /login. Without this guard the
  //   interceptor fires another refresh on every refresh-401, creating a
  //   tight loop of POST /api/auth/refresh calls.
  const requestUrl = originalRequest?.url || '';
  const isAuthMeRequest = requestUrl.includes('/auth/me');
  const isImpersonateConsume = requestUrl.includes('/auth/impersonate');
  const isAuthRefreshRequest = requestUrl.includes('/auth/refresh');
  const skipInterceptor = isImpersonateConsume || isAuthRefreshRequest;

  // 403: immediate logout, no retry. /auth/me is left to the route guards.
  if (status === 403 && !isAuthPage() && !skipInterceptor && !isAuthMeRequest) {
    endSession();
    throw new HttpError(status, data);
  }

  // 401: try refresh once, then retry original request. This covers /auth/me
  // too, so an expired access cookie with a live refresh cookie keeps the
  // session. Auth pages are skipped, so a failed refresh there cannot loop.
  if (status === 401 && !isAuthPage() && !skipInterceptor && !originalRequest._retry) {
    originalRequest._retry = true;
    try {
      await refreshSession();
    } catch (refreshError) {
      // /auth/me: the route guard sends the user to /login itself. Doing a
      // hard redirect here too would race it.
      if (!isAuthMeRequest) {
        endSession();
      }
      throw refreshError;
    }
    return axiosInstance(originalRequest);
  }

  throw new HttpError(status, data);
};

export function setupInterceptorsTo(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use(onRequest, onRequestError);
  instance.interceptors.response.use(onResponse, onResponseError);
  return instance;
}

const instance = axios.create({
  withCredentials: true,
});

export const axiosInstance = setupInterceptorsTo(instance);

configureRefreshTimer({ refresh: refreshSession, endSession });
